// Süper Admin Paneli (şirket listesi) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'superadmin.panel';

export const superAdminPanelGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Süper Admin Paneli nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Platformdaki tüm şirketleri bu sayfadan izler ve yönetirsiniz: şirket açma, demo ve sözleşme takibi, personel limiti, modüller, bayi ataması ve yazarkasalar. Bayi olarak girdiyseniz yalnız kendi şirketlerinizi görürsünüz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Arama, toplam ve Şirket Oluştur'),
    stats: t(`${G}.regions.stats`, 'Sekmeler'),
    filters: t(`${G}.regions.filters`, 'Filtreler ve sıralama'),
    toolbar: t(`${G}.regions.toolbar`, 'Kart / liste görünümü'),
    table: t(`${G}.regions.table`, 'Şirketler ve işlem menüsü'),
    pagination: t(`${G}.regions.pagination`, 'Yazarkasa listesi'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Şirket arayın, yeni şirket açın'),
      text: t(`${G}.s1.text`, '"Şirket ara..." kutusuna şirket adını yazın; yanında "Toplam Şirket" sayısı görünür. "Şirket Oluştur" ile yeni şirket ve yöneticisini açarsınız.'),
      tips: [
        t(`${G}.s1.tip1`, 'Bayi olarak girdiyseniz "Şirket Kotası: kullanılan / kota" kutusu görünür. Kota dolduğunda "Şirket Oluştur" kapanır; yeni şirket için merkezden kota isteyin.'),
        t(`${G}.s1.tip2`, 'Bayinin açtığı şirkette modüller kapalı gelir; merkez modül izni verene kadar şirket modülleri kullanamaz.'),
      ],
    },
    {
      region: 'stats',
      title: t(`${G}.s2.title`, '2. Sekmeyle listeyi daraltın'),
      text: t(`${G}.s2.text`, 'Tüm Şirketler: hepsi. Demo: demo bitiş tarihi verilmiş şirketler. Aktif Şirketler: son 7 günde giriş yapanlar. Sözleşmeli Şirketler: sözleşmesi girilmiş olanlar. Yazarkasa Verilen Şirketler: yazarkasa cihazı tanımlı olanlar. Yazarkasalar / Bayi Yazarkasaları: cihaz listesi (adım 6).'),
      tips: [
        t(`${G}.s2.tip1`, 'Aktif Şirketler ve Bayi Yazarkasaları sekmeleri yalnız süper admine görünür; bayide "Yazarkasalar" sekmesi "Yazarkasalarım" adını alır.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s3.title`, '3. Filtreleyin ve sıralayın'),
      text: t(`${G}.s3.text`, 'Tüm Şirketler sekmesinde Şirket ve Bayi seçebilirsiniz. Başlangıç / Bitiş Tarihi şirketin son giriş tarihine göre süzer. "Demo günü" kalan demo süresine (Demo değil, Demo doldu, 1–7, 8–15, 16–30, 30+ gün), "Durum" erişime göre süzer. "Sırala" ve yanındaki ok düğmesiyle sıralama alanını ve yönünü seçin; "Filtreleri Temizle" hepsini sıfırlar.'),
      tips: [
        t(`${G}.s3.tip1`, 'Durum "Pasif (demo doldu)": demosu ya da aboneliği bitmiş, erişimi kilitli şirketler. "Süre doldu (son gün)" etiketli şirketin süresi dolmuştur ama kilit henüz devreye girmemiştir.'),
        t(`${G}.s3.tip2`, 'Şirket ve Bayi filtreleri yalnız Tüm Şirketler sekmesindedir; başka sekmeye geçince temizlenir.'),
        t(`${G}.s3.tip3`, 'Hatırlatma bildiriminden gelirseniz liste o kayda iner; "Filtreyi Kaldır" ile tüm listeye dönün.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Şirket işlemleri: personel limiti, modül, demo'),
      text: t(`${G}.s4.text`, 'Şirket kartına (ya da liste satırına) tıklamak şirket detaylarını açar. Kartın / satırın menüsünde şirketin işlemleri bulunur: Personel Sayısını Güncelle, Yazarkasa Cihazları, Sözleşme Yapıldı (sözleşme varsa Sözleşmeyi Güncelle), AI Chat, Modüller, Bayiye Ata, SQL\'e Senkronize Et ve Şirketi Sil.'),
      tips: [
        t(`${G}.s4.tip1`, 'Personel Sayısını Güncelle: şirketin tanımlayabileceği en fazla personel sayısını (personel limiti) değiştirir.'),
        t(`${G}.s4.tip2`, 'Modüller: şirketin hangi modülleri kullanacağını ve demo bitiş tarihini buradan verirsiniz. Demo sekmesindeki şirketler bu tarihe göre listelenir.'),
        t(`${G}.s4.tip3`, 'Bayi görünümünde menüde yalnız Personel Sayısını Güncelle, sözleşme ve Modüller bulunur; diğer işlemler süper admine özeldir.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Sözleşme, bayi ataması ve silme'),
      text: t(`${G}.s5.text`, '"Şirket Sözleşmesi" penceresinde Sözleşme Başlangıç ve Bitiş Tarihini girip "Kaydet" deyin; mevcut sözleşme varsa tarihleri ve kalan gün görünür. Süper adminde aynı pencerede şirketin modül ve ek ürün fiyatları da girilir; bu fiyatlar sözleşme tarihleriyle birlikte kaydedilir.'),
      tips: [
        t(`${G}.s5.tip1`, 'Bayiye Ata: şirketi seçtiğiniz bayiye bağlar. Şirket başka bir bayideyse ataması yeni bayiye taşınır; "Atamayı Kaldır" şirketi bayiden ayırır.'),
        t(`${G}.s5.tip2`, 'Şirketi Sil geri alınamaz: şirket verileri kalıcı silinir, personel hesapları korunur. Onay için şirket adını birebir yazmanız gerekir. Sözleşmesi olan şirket silinemez.'),
      ],
    },
    {
      region: 'pagination',
      title: t(`${G}.s6.title`, '6. Yazarkasalar sekmesi'),
      text: t(`${G}.s6.text`, 'Cihaz, şirket, marka, model veya hat no ile arayın; Şirket ve (şirket seçtikten sonra) Şube ile süzün. Süresi dolmuş cihazlar kırmızı, 30 gün içinde dolacaklar turuncu renkte görünür. "Yeni Cihaz Ekle" ile cihaz ekleyin, "Yenile" ile listeyi tazeleyin, "Entegrasyonlar" ile şubelerin entegrasyon özetini açın.'),
      tips: [
        t(`${G}.s6.tip1`, 'Bayi Yazarkasaları sekmesi bayilerin girdiği cihazları ayrı gösterir; burada Bayi filtresi vardır, cihaz ekleme düğmesi yoktur. Merkezin cihazları ana sekmeden eklenir.'),
        t(`${G}.s6.tip2`, '"Bu sayfada ... Süresi Dolmuş / Yaklaşıyor" etiketleri yalnız ekrandaki sayfayı sayar.'),
      ],
    },
  ],
});
